import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import AddressAutocomplete from './AddressAutocomplete';
import { parseUserDateTime, formatDateTime } from '../../utils/dateUtils';
import { useAuth } from '../../contexts/AuthContext';

// Helper to get the next day's date in YYYY-MM-DD format
const getNextDay = (dateStr) => {
  const [year, month, day] = dateStr.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  date.setDate(date.getDate() + 1);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

// Separate component for PaymentElement form (needs its own Elements context)
const DayBookingPaymentForm = ({ onSubmit, onBack, loading }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [localError, setLocalError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setLocalError('');

    // Confirm SetupIntent to save payment method
    const { error: stripeError, setupIntent } = await stripe.confirmSetup({
      elements,
      redirect: 'if_required',
      confirmParams: {
        return_url: window.location.href,
      },
    });

    if (stripeError) {
      setLocalError(stripeError.message);
      return;
    }

    // Pass the payment method ID back to parent
    onSubmit(setupIntent.payment_method);
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="mb-6">
        <h3 className="font-semibold mb-3">Payment Method</h3>
        <div className="p-4 border rounded">
          <PaymentElement />
        </div>
        {localError && (
          <p className="text-red-500 text-sm mt-2">{localError}</p>
        )}
      </div>

      <div className="flex gap-3">
        <button
          type="button"
          onClick={onBack}
          className="flex-1 px-6 py-3 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 font-semibold"
        >
          Back
        </button>
        <button
          type="submit"
          disabled={!stripe || loading}
          className="flex-1 px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-semibold disabled:bg-gray-400"
        >
          {loading ? 'Processing...' : 'Confirm & Pay'}
        </button>
      </div>

      <p className="text-xs text-gray-500 mt-4 text-center">
        Note: Day bookings follow the same cancellation policy as regular rides. Payment will be held until the driver accepts your request.
      </p>
    </form>
  );
};

const DayBookingRequestForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();

  // State for selected driver
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [loadingDriver, setLoadingDriver] = useState(true);
  const [redirecting, setRedirecting] = useState(false);

  const [quote, setQuote] = useState(null);
  const [formData, setFormData] = useState({
    rate_type: 'half_day',  // 'half_day' or 'full_day'
    start_date: ''
  });
  const [legs, setLegs] = useState([
    {
      pickup_address: '',
      pickup_lat: null,
      pickup_lng: null,
      dropoff_address: '',
      dropoff_lat: null,
      dropoff_lng: null,
      pickup_time: '',
      estimated_duration_minutes: 30,
      distance_miles: 0
    }
  ]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [durationError, setDurationError] = useState('');
  const [step, setStep] = useState(1); // 1: Form, 2: Quote, 3: Payment
  const [clientSecret, setClientSecret] = useState(null);
  const [setupIntentId, setSetupIntentId] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);

  // Fetch SetupIntent when entering quote/payment step
  useEffect(() => {
    const fetchSetupIntent = async () => {
      if (step === 2 && !clientSecret) {
        setPaymentLoading(true);
        try {
          const response = await axios.post(
            `${process.env.REACT_APP_API_URL}/api/riders/payment/setup-intent`,
            {}  // Empty body ensures Content-Type header is sent
          );
          setClientSecret(response.data.client_secret);
          setSetupIntentId(response.data.setup_intent_id);
        } catch (error) {
          console.error('Error creating setup intent:', error);
          setError('Failed to initialize payment. Please try again.');
        } finally {
          setPaymentLoading(false);
        }
      }
    };
    fetchSetupIntent();
  }, [step, clientSecret]);

  // Load driver from URL parameter or navigation state or sessionStorage
  useEffect(() => {
    const loadDriver = async () => {
      setLoadingDriver(true);

      const driverIdFromUrl = searchParams.get('driver_id');
      const driverFromState = location.state?.driver;
      const driverIdFromSession = sessionStorage.getItem('intended_driver_id');

      if (driverFromState) {
        setSelectedDriver(driverFromState);
        setLoadingDriver(false);
        if (driverIdFromUrl) {
          sessionStorage.setItem('intended_driver_id', driverIdFromUrl);
        }
      } else if (driverIdFromUrl || driverIdFromSession) {
        const driverId = driverIdFromUrl || driverIdFromSession;

        if (driverIdFromUrl) {
          sessionStorage.setItem('intended_driver_id', driverIdFromUrl);
        }

        const token = localStorage.getItem('token');

        if (!token) {
          console.log('Not authenticated, redirecting to signup...');
          setRedirecting(true);
          setError('Please create an account or log in to book a day rate.');
          setTimeout(() => {
            navigate(`/signup/rider?driver_id=${driverId}`);
          }, 2000);
          return;
        }

        try {
          const response = await axios.get(
            `${process.env.REACT_APP_API_URL}/api/riders/drivers/${driverId}`,
            {
              headers: {
                Authorization: `Bearer ${token}`
              }
            }
          );

          setSelectedDriver(response.data);

          if (driverIdFromSession) {
            sessionStorage.removeItem('intended_driver_id');
          }
        } catch (error) {
          console.error('Error loading driver:', error);
          setError('Failed to load driver information.');
        } finally {
          setLoadingDriver(false);
        }
      } else {
        setError('No driver selected. Please browse drivers and select one.');
        setLoadingDriver(false);
      }
    };

    loadDriver();
  }, [searchParams, location, navigate]);

  const addLeg = () => {
    setLegs([...legs, {
      pickup_address: '',
      pickup_lat: null,
      pickup_lng: null,
      dropoff_address: '',
      dropoff_lat: null,
      dropoff_lng: null,
      pickup_time: '',
      estimated_duration_minutes: 30,
      distance_miles: 0
    }]);
  };

  const removeLeg = (index) => {
    if (legs.length > 1) {
      setLegs(legs.filter((_, i) => i !== index));
    }
  };

  const updateLeg = (index, field, value) => {
    const newLegs = [...legs];
    newLegs[index][field] = value;
    setLegs(newLegs);
  };

  const handlePickupSelect = async (index, place) => {
    const newLegs = [...legs];
    newLegs[index].pickup_address = place.formatted_address;
    newLegs[index].pickup_lat = place.geometry.location.lat();
    newLegs[index].pickup_lng = place.geometry.location.lng();
    setLegs(newLegs);

    // Calculate distance if both pickup and dropoff are set
    if (newLegs[index].dropoff_lat && newLegs[index].dropoff_lng) {
      await calculateDistance(index, newLegs);
    }
  };

  const handleDropoffSelect = async (index, place) => {
    const newLegs = [...legs];
    newLegs[index].dropoff_address = place.formatted_address;
    newLegs[index].dropoff_lat = place.geometry.location.lat();
    newLegs[index].dropoff_lng = place.geometry.location.lng();
    setLegs(newLegs);

    // Calculate distance if both pickup and dropoff are set
    if (newLegs[index].pickup_lat && newLegs[index].pickup_lng) {
      await calculateDistance(index, newLegs);
    }
  };

  // Calculate estimated dropoff time for a leg
  // Helper to extract error message from API response
  const extractErrorMessage = (error) => {
    if (error.response?.data?.detail) {
      const detail = error.response.data.detail;

      // If detail is a string, return it directly
      if (typeof detail === 'string') {
        return detail;
      }

      // If detail is an array (FastAPI validation errors), extract messages
      if (Array.isArray(detail)) {
        return detail.map(err => err.msg || JSON.stringify(err)).join(', ');
      }

      // If detail is an object, try to extract message
      if (typeof detail === 'object') {
        return detail.msg || detail.message || JSON.stringify(detail);
      }
    }

    return null;
  };

  const calculateDropoffTime = (pickupTime, durationMinutes) => {
    if (!pickupTime || !durationMinutes) return null;

    try {
      const [hours, minutes] = pickupTime.split(':').map(Number);
      const totalMinutes = hours * 60 + minutes + durationMinutes;
      const dropoffHours = Math.floor(totalMinutes / 60) % 24;
      const dropoffMinutes = totalMinutes % 60;

      // Format time in 12-hour format with AM/PM
      const period = dropoffHours >= 12 ? 'PM' : 'AM';
      const displayHours = dropoffHours === 0 ? 12 : (dropoffHours > 12 ? dropoffHours - 12 : dropoffHours);
      const displayMinutes = dropoffMinutes.toString().padStart(2, '0');

      return `${displayHours}:${displayMinutes} ${period} CT`;
    } catch (error) {
      console.error('Error calculating dropoff time:', error);
      return null;
    }
  };

  const calculateDistance = async (index, legsData = legs) => {
    const leg = legsData[index];
    if (!leg.pickup_lat || !leg.dropoff_lat) return;

    try {
      const service = new window.google.maps.DistanceMatrixService();
      const request = {
        origins: [{ lat: leg.pickup_lat, lng: leg.pickup_lng }],
        destinations: [{ lat: leg.dropoff_lat, lng: leg.dropoff_lng }],
        travelMode: 'DRIVING',
        unitSystem: window.google.maps.UnitSystem.IMPERIAL
      };

      service.getDistanceMatrix(request, (response, status) => {
        if (status === 'OK' && response.rows[0].elements[0].status === 'OK') {
          const distanceMeters = response.rows[0].elements[0].distance.value;
          const distanceMiles = (distanceMeters / 1609.34).toFixed(2);
          const durationSeconds = response.rows[0].elements[0].duration.value;
          const durationMinutes = Math.ceil(durationSeconds / 60);

          // Use functional state update to ensure we're working with the latest state
          setLegs(prevLegs => {
            const updatedLegs = [...prevLegs];
            updatedLegs[index].distance_miles = parseFloat(distanceMiles);
            updatedLegs[index].estimated_duration_minutes = durationMinutes;
            return updatedLegs;
          });
        }
      });
    } catch (error) {
      console.error('Error calculating distance:', error);
    }
  };

  // Calculate total trip duration based on legs
  const calculateTripDuration = () => {
    if (!formData.start_date || legs.length === 0) return null;

    // Filter legs with pickup times and durations
    const validLegs = legs.filter(leg => leg.pickup_time && leg.estimated_duration_minutes > 0);

    if (validLegs.length === 0) return null;

    // Find earliest pickup time (start of day booking)
    const startTime = validLegs
      .map(leg => leg.pickup_time)
      .sort()[0];

    // Find the leg with the LATEST pickup time to determine end time
    const latestLeg = validLegs.reduce((latest, current) => {
      const latestMinutes = latest.pickup_time.split(':').reduce((h, m) => parseInt(h) * 60 + parseInt(m));
      const currentMinutes = current.pickup_time.split(':').reduce((h, m) => parseInt(h) * 60 + parseInt(m));
      return currentMinutes > latestMinutes ? current : latest;
    });

    // Calculate end time: latest pickup + that leg's estimated duration
    const [latestHours, latestMinutes] = latestLeg.pickup_time.split(':').map(Number);
    const totalMinutesAtPickup = latestHours * 60 + latestMinutes;
    const totalMinutesAtDropoff = totalMinutesAtPickup + latestLeg.estimated_duration_minutes;

    const [startHours, startMinutes] = startTime.split(':').map(Number);
    const startTotalMinutes = startHours * 60 + startMinutes;

    const durationMinutes = totalMinutesAtDropoff - startTotalMinutes;
    const durationHours = durationMinutes / 60;

    // Handle midnight rollover - if end time goes past 24:00, it's the next day
    const endTimeNextDay = totalMinutesAtDropoff >= 1440;
    const normalizedEndMinutes = endTimeNextDay ? totalMinutesAtDropoff - 1440 : totalMinutesAtDropoff;

    return {
      startTime,
      endTime: `${String(Math.floor(normalizedEndMinutes / 60)).padStart(2, '0')}:${String(normalizedEndMinutes % 60).padStart(2, '0')}`,
      endTimeNextDay,
      durationHours: durationHours.toFixed(2)
    };
  };

  // Validate trip duration
  const validateDuration = () => {
    const tripDuration = calculateTripDuration();
    if (!tripDuration) {
      setDurationError('');
      return true;
    }

    const maxHours = formData.rate_type === 'half_day' ? 6.5 : 12.5;
    const durationHours = parseFloat(tripDuration.durationHours);

    if (formData.rate_type === 'half_day' && durationHours > 6.5) {
      setDurationError("This trip's duration is too long for a half-day booking. Please book a full-day instead.");
      return false;
    } else if (formData.rate_type === 'full_day' && durationHours > 12.5) {
      setDurationError("This trip's duration is longer than the system can handle. Please shorten your trip.");
      return false;
    }

    setDurationError('');
    return true;
  };

  // Re-validate when rate type or legs change
  useEffect(() => {
    validateDuration();
  }, [formData.rate_type, legs]);

  const getQuote = async () => {
    setError('');
    setLoading(true);

    // Validate date is filled
    if (!formData.start_date) {
      setError('Please select a date');
      setLoading(false);
      return;
    }

    // Validate date is not in the past
    // Parse selected date as local time (not UTC) by splitting the date string
    const [year, month, day] = formData.start_date.split('-').map(Number);
    const selectedDate = new Date(year, month - 1, day, 0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (selectedDate < today) {
      setError('Cannot book for a past date. Please select today or a future date.');
      setLoading(false);
      return;
    }

    // Validate all legs
    for (let i = 0; i < legs.length; i++) {
      const leg = legs[i];
      if (!leg.pickup_address || !leg.dropoff_address || !leg.pickup_time) {
        setError(`Please complete all fields for Trip ${i + 1}`);
        setLoading(false);
        return;
      }
      if (leg.distance_miles === 0) {
        setError(`Please select valid addresses for Trip ${i + 1}`);
        setLoading(false);
        return;
      }
    }

    // Validate duration
    if (!validateDuration()) {
      setError(durationError);
      setLoading(false);
      return;
    }

    // Calculate start and end times from legs
    const tripDuration = calculateTripDuration();
    if (!tripDuration) {
      setError('Unable to calculate trip duration');
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const startDateTime = parseUserDateTime(formData.start_date, tripDuration.startTime);
      // Use next day's date if end time crosses midnight
      const endDate = tripDuration.endTimeNextDay ? getNextDay(formData.start_date) : formData.start_date;
      const endDateTime = parseUserDateTime(endDate, tripDuration.endTime);

      // Build legs payload with full datetime for pickup_time
      const legsPayload = legs.map(leg => ({
        pickup_address: leg.pickup_address,
        pickup_lat: leg.pickup_lat,
        pickup_lng: leg.pickup_lng,
        dropoff_address: leg.dropoff_address,
        dropoff_lat: leg.dropoff_lat,
        dropoff_lng: leg.dropoff_lng,
        pickup_time: parseUserDateTime(formData.start_date, leg.pickup_time),
        distance_miles: leg.distance_miles
      }));

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/day-bookings/rider/quote`,
        {
          driver_id: selectedDriver.id,
          rate_type: formData.rate_type,
          booking_date: formData.start_date,
          start_time: startDateTime,
          end_time: endDateTime,
          legs: legsPayload
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      setQuote(response.data);
      setStep(2);
    } catch (error) {
      console.error('Error getting quote:', error);
      const errorMessage = extractErrorMessage(error);
      setError(errorMessage || 'Failed to get quote. Please check your information and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentAndBooking = async (paymentMethodId) => {
    setError('');
    setLoading(true);

    try {
      const tripDuration = calculateTripDuration();
      const token = localStorage.getItem('token');
      const startDateTime = parseUserDateTime(formData.start_date, tripDuration.startTime);
      // Use next day's date if end time crosses midnight
      const endDate = tripDuration.endTimeNextDay ? getNextDay(formData.start_date) : formData.start_date;
      const endDateTime = parseUserDateTime(endDate, tripDuration.endTime);

      const legsPayload = legs.map(leg => ({
        pickup_address: leg.pickup_address,
        pickup_lat: leg.pickup_lat,
        pickup_lng: leg.pickup_lng,
        dropoff_address: leg.dropoff_address,
        dropoff_lat: leg.dropoff_lat,
        dropoff_lng: leg.dropoff_lng,
        pickup_time: parseUserDateTime(formData.start_date, leg.pickup_time),
        distance_miles: leg.distance_miles
      }));

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/day-bookings/rider/request`,
        {
          driver_id: selectedDriver.id,
          rate_type: formData.rate_type,
          booking_date: formData.start_date,  // Add booking_date field
          start_time: startDateTime,
          end_time: endDateTime,
          legs: legsPayload,
          payment_method_id: paymentMethodId
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      // Success - redirect to rider dashboard or confirmation page
      window.alert('Day booking request submitted successfully! The driver will be notified.');
      navigate('/rider/dashboard');
    } catch (error) {
      console.error('Error submitting booking:', error);
      const errorMessage = extractErrorMessage(error);
      setError(errorMessage || 'Failed to submit booking request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (user?.is_active === false) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow p-8 max-w-md text-center">
          <h2 className="text-xl font-bold text-red-700 mb-3">Account Deactivated</h2>
          <p className="text-gray-600 mb-4">Your account has been deactivated. You cannot request day bookings.</p>
          <p className="text-sm text-gray-500 mb-6">Contact support@ridehowl.com for assistance.</p>
          <button onClick={() => navigate('/rider/dashboard')} className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700">
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (loadingDriver) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-xl">Loading driver information...</div>
      </div>
    );
  }

  if (redirecting) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow">
          <p className="text-xl text-gray-900 mb-4">{error}</p>
          <p className="text-gray-600">Redirecting to signup...</p>
        </div>
      </div>
    );
  }

  if (!selectedDriver) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow">
          <p className="text-xl text-red-600 mb-4">{error || 'No driver selected'}</p>
          <button
            onClick={() => navigate('/rider/browse-drivers')}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Browse Drivers
          </button>
        </div>
      </div>
    );
  }

  const maxHours = formData.rate_type === 'half_day' ? 6 : 12;
  const tripDuration = calculateTripDuration();

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate(-1)} className="text-gray-600 hover:text-gray-900">
              ← Back
            </button>
            <h1 className="text-2xl font-bold text-gray-900">Request Day Booking</h1>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Helpful Tip */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-2">
            <svg className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-sm text-blue-700">
              <span className="font-semibold">Tip:</span> To view this driver's hourly availability for a specific date, visit the{' '}
              <button
                onClick={() => navigate(`/rider/request-ride?driver_id=${selectedDriver.id}`)}
                className="underline font-medium hover:text-blue-900"
              >
                Regular Ride Request page
              </button>{' '}
              and select your date to see their schedule.
            </p>
          </div>
        </div>

        {/* Driver Info */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-semibold mb-2">Driver Information</h2>
          <p className="text-gray-900">{selectedDriver.first_name} {selectedDriver.last_name}</p>
          <p className="text-sm text-gray-600">{selectedDriver.vehicle_make} {selectedDriver.vehicle_model}</p>
        </div>

        {/* Step 1: Booking Form */}
        {step === 1 && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold mb-6">Booking Details</h2>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
                {error}
              </div>
            )}

            {durationError && (
              <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded mb-4">
                {durationError}
              </div>
            )}

            {/* Rate Type Selection */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Booking Type
              </label>
              <div className="flex gap-4">
                <button
                  onClick={() => setFormData({ ...formData, rate_type: 'half_day' })}
                  className={`flex-1 px-4 py-3 rounded-lg border-2 ${
                    formData.rate_type === 'half_day'
                      ? 'border-orange-500 bg-orange-50 text-orange-900'
                      : 'border-gray-300 bg-white text-gray-700'
                  }`}
                >
                  <div className="font-semibold">Half-Day</div>
                  <div className="text-sm">Up to 6 hours</div>
                </button>
                <button
                  onClick={() => setFormData({ ...formData, rate_type: 'full_day' })}
                  className={`flex-1 px-4 py-3 rounded-lg border-2 ${
                    formData.rate_type === 'full_day'
                      ? 'border-orange-500 bg-orange-50 text-orange-900'
                      : 'border-gray-300 bg-white text-gray-700'
                  }`}
                >
                  <div className="font-semibold">Full-Day</div>
                  <div className="text-sm">Up to 12 hours</div>
                </button>
              </div>
            </div>

            {/* Date Selection */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date
              </label>
              <input
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                min={new Date().toISOString().split('T')[0]}
                className="max-w-[280px] sm:max-w-none w-full px-3 py-2 border rounded focus:ring-2 focus:ring-orange-500"
                required
              />
            </div>

            {/* Auto-calculated time range display */}
            {tripDuration && (
              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded">
                <h3 className="text-sm font-semibold text-blue-900 mb-2">Calculated Time Range</h3>
                <div className="text-sm text-blue-800">
                  <p>Start: {tripDuration.startTime}</p>
                  <p>Estimated End: {tripDuration.endTime}{tripDuration.endTimeNextDay ? ' (next day)' : ''}</p>
                  <p>Duration: {tripDuration.durationHours} hours</p>
                </div>
              </div>
            )}

            {/* Trip Legs */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-4">Trip Details</h3>
              {legs.map((leg, index) => (
                <div key={index} className="mb-6 p-4 border-2 border-orange-200 rounded-lg bg-orange-50">
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="font-semibold text-gray-900">Trip {index + 1}</h4>
                    {legs.length > 1 && (
                      <button
                        onClick={() => removeLeg(index)}
                        className="text-red-600 hover:text-red-800 text-sm"
                      >
                        Remove
                      </button>
                    )}
                  </div>

                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Pickup Time
                      </label>
                      <input
                        type="time"
                        value={leg.pickup_time}
                        onChange={(e) => updateLeg(index, 'pickup_time', e.target.value)}
                        className="max-w-[200px] sm:max-w-none w-full px-3 py-2 border rounded focus:ring-2 focus:ring-orange-500"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Pickup Address
                      </label>
                      <AddressAutocomplete
                        value={leg.pickup_address}
                        onChange={(address) => updateLeg(index, 'pickup_address', address)}
                        onPlaceSelect={(place) => handlePickupSelect(index, place)}
                        placeholder="Enter pickup address"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Dropoff Address
                      </label>
                      <AddressAutocomplete
                        value={leg.dropoff_address}
                        onChange={(address) => updateLeg(index, 'dropoff_address', address)}
                        onPlaceSelect={(place) => handleDropoffSelect(index, place)}
                        placeholder="Enter dropoff address"
                      />
                    </div>

                    {leg.distance_miles > 0 && (
                      <div className="text-sm text-gray-600 space-y-1">
                        <div>Distance: {leg.distance_miles} miles</div>
                        {leg.estimated_duration_minutes > 0 && (
                          <>
                            <div>Est. Duration: {leg.estimated_duration_minutes} min</div>
                            {leg.pickup_time && calculateDropoffTime(leg.pickup_time, leg.estimated_duration_minutes) && (
                              <div>Est. Dropoff: {calculateDropoffTime(leg.pickup_time, leg.estimated_duration_minutes)}</div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}

              <button
                onClick={addLeg}
                className="w-full px-4 py-2 border-2 border-dashed border-orange-300 text-orange-700 rounded-lg hover:bg-orange-50"
              >
                + Add Another Trip
              </button>
            </div>

            <button
              onClick={getQuote}
              disabled={loading || !!durationError}
              className="w-full px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-semibold disabled:bg-gray-400"
            >
              {loading ? 'Loading...' : 'Get Quote'}
            </button>
          </div>
        )}

        {/* Step 2: Quote Display */}
        {step === 2 && quote && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold mb-6">Booking Quote</h2>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
                {error}
              </div>
            )}

            <div className="space-y-3 mb-6">
              <div className="flex justify-between">
                <span className="text-gray-600">Rate Type:</span>
                <span className="font-semibold">{quote.rate_type === 'half_day' ? 'Half-Day (6 hours)' : 'Full-Day (12 hours)'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Duration:</span>
                <span className="font-semibold">{quote.duration_hours} hours</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Total Mileage:</span>
                <span className="font-semibold">{quote.total_mileage} miles (Tier {quote.mileage_tier_used})</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Number of Trips:</span>
                <span className="font-semibold">{legs.length}</span>
              </div>
              <div className="border-t pt-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Flat Rate:</span>
                  <span>${Number(quote.flat_rate).toFixed(2)}</span>
                </div>
                {quote.sales_tax > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Tax:</span>
                    <span>${Number(quote.sales_tax).toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-600">Stripe Processing Fee:</span>
                  <span>${Number(quote.fare_processing_fee).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-lg font-bold mt-2">
                  <span>Total:</span>
                  <span className="text-orange-600">${Number(quote.rider_total).toFixed(2)}</span>
                </div>
              </div>
            </div>

            <div className="border-t pt-4 mb-6">
              <h3 className="font-semibold mb-3">Trip Summary</h3>
              {legs.map((leg, index) => (
                <div key={index} className="text-sm text-gray-600 mb-2">
                  <span className="font-medium">Trip {index + 1}:</span> {leg.pickup_address} → {leg.dropoff_address} ({leg.distance_miles} mi)
                </div>
              ))}
            </div>

            {/* Payment Info Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-2 mb-2">
                <svg className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h4 className="font-semibold text-blue-800">Payment Information</h4>
              </div>
              <ul className="text-sm text-blue-700 space-y-1.5 ml-7">
                <li>Your payment method will be <strong>authorized</strong> when the driver accepts your request</li>
                <li>For bookings more than 6 days out, your payment method is saved securely and authorized closer to the date</li>
                <li>Final charge occurs within 24 hours of completing the final leg</li>
                <li>You'll have <strong>24 hours after completion</strong> to add an optional tip</li>
              </ul>
            </div>

            {paymentLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Loading payment options...</p>
              </div>
            ) : clientSecret ? (
              <Elements stripe={stripePromise} options={{ clientSecret, appearance: { theme: 'stripe' } }}>
                <DayBookingPaymentForm
                  onSubmit={handlePaymentAndBooking}
                  onBack={() => setStep(1)}
                  loading={loading}
                />
              </Elements>
            ) : (
              <div className="text-center py-8 text-red-600">
                <p>Failed to load payment options. Please try again.</p>
                <button
                  onClick={() => { setClientSecret(null); setStep(2); }}
                  className="mt-4 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
                >
                  Retry
                </button>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

const RequestDayBooking = () => {
  return (
    <Elements stripe={stripePromise}>
      <DayBookingRequestForm />
    </Elements>
  );
};

export default RequestDayBooking;
